import React, { memo, useMemo, useEffect, useState, useCallback }  from 'react';
// import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import GaugeChart from 'react-gauge-chart';

import SkeletonLoader from "../../../../../ui/SkeletonLoader";

import { trendingDownIcon, trendingUpIcon } from "../../../../../../utils/icons";
import { formatDate } from "../../../../../../utils/util";

import { useScanInfoContext } from "../../../../../../pages/ScanInfo/ScanInfoContext";

import { useSelector, useDispatch } from 'react-redux';
import { getOverallRiskScore, getRiskScoreOverview } from "../../../../../../store/actions/thirdPartyRiskAction";

const Gauge = memo(({ percent }) => {
    const chartStyle = useMemo(() => ({ height: 54, width: 120 }), []);

    const gaugeProps = useMemo(
        () => ({
            id: "gauge-chart1",
            nrOfLevels: 4,
            colors: ["#ee6862", "#f2a16b", "#F4DA6B", "#8dd761"],
            percent,
            arcWidth: 0.2,
            arcPadding: 0,
            cornerRadius: 0,
            hideText: true,
            style: chartStyle,
        }),
        [percent, chartStyle]
    );

    return <GaugeChart {...gaugeProps} />;
});

const DomainRiskScore = () =>{

    const {
        domainId,
        scanId,
        organizationId,
        triggerStatsFetch,
        setTriggerStatsFetch,
    } = useScanInfoContext();

    const dispatch = useDispatch();

    const overallRiskScoreResponse = useSelector((state) => state.thirdPartyRisk.overAllRiskScore);
    const overallRiskScoreOverviewResponse = useSelector((state) => state.thirdPartyRisk.riskScoreOverview);

    const [riskOverallScoreLoading, setRiskOverallScoreLoadingLoading] = useState(false);
    const [overViewLoading, setOverviewLoading] = useState(false);
    const [overallRiskScore, setOverallRiskScore] = useState(null);
    const [overallRiskScoreOverview, setOverallRiskScoreOverview] = useState(null);
    const [hoveredItem, setHoveredItem] = useState(null);

    const fetchOverallRiskScore = useCallback(() => {
        setRiskOverallScoreLoadingLoading(true);
        const params = organizationId.toLowerCase() !== "all" && domainId.toLowerCase() === "all" ? `&organization_id=${organizationId}`: `&domain=${domainId}&scan_id=${scanId}`;
        dispatch(getOverallRiskScore(params))
    },[organizationId, domainId, scanId, dispatch])

    const fetchOverallScoreOverview = useCallback(()=>{
        setOverviewLoading(true);
        const params = organizationId.toLowerCase() !== "all" && domainId.toLowerCase() === "all" ? `&organization_id=${organizationId}`: `&domain=${domainId}`;
        dispatch(getRiskScoreOverview(params))
    },[organizationId, domainId, dispatch])

    useEffect(() => {            
        if (triggerStatsFetch) {
            fetchOverallRiskScore()
            fetchOverallScoreOverview()
            setTriggerStatsFetch(false);
        }
    }, [triggerStatsFetch, setTriggerStatsFetch, fetchOverallRiskScore, fetchOverallScoreOverview]);

    useEffect(()=>{        
        if(overallRiskScoreResponse !== null) {
            setRiskOverallScoreLoadingLoading(false);
            setOverallRiskScore(overallRiskScoreResponse);
        }
    },[overallRiskScoreResponse])

    useEffect(()=>{
        if(overallRiskScoreOverviewResponse !== null) {
            setOverviewLoading(false);
            setOverallRiskScoreOverview(overallRiskScoreOverviewResponse);
        }
    },[overallRiskScoreOverviewResponse])

    return (
        <div className="bg-white-with-shadow p-4 h-100">
            <div className="domain-risk-score-container">
                {
                    riskOverallScoreLoading ?
                    <SkeletonLoader type={"line"} count={7} currentPage={"overallRiskScore"} />
                    :
                    <motion.div
                        className="h-100"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <p className="heading">Domain risk score</p>
                        <div className="d-flex align-items-center gap-5">
                            <div className="gauage-container mt-3">
                                <Gauge percent={Number(overallRiskScore?.stats?.domain_risk_score?.overall_score || 0) / 900} />
                                <p className="hacker-score my-2">{overallRiskScore?.stats?.domain_risk_score?.overall_score} <label>Max Score 900</label></p>
                                <p className={`risk-level ${overallRiskScore?.stats?.domain_risk_score?.risk_level}`}>Risk level: {overallRiskScore?.stats?.domain_risk_score?.risk_level}</p>
                            </div>
                            <div className="previous-scan-result">
                                <p className="score-difference mb-3">{overallRiskScore?.stats?.domain_risk_score?.percentage_change}% {overallRiskScore?.stats?.domain_risk_score?.percentage !== 0 && <span>{overallRiskScore?.stats?.domain_risk_score?.trend === "decreased" ? trendingDownIcon() : trendingUpIcon()}</span>}</p>
                                <p className="scan-text">vs  Previous Scan</p>
                            </div>
                        </div>
                        <div className="scan-date-details">
                            <p className="scan-date">Last scan date: <label>{formatDate(overallRiskScore?.stats?.domain_risk_score?.scan_date, "date")}</label></p>
                            <p className="scan-date">Previous scan date: <label>{formatDate(overallRiskScore?.stats?.domain_risk_score?.previous_scan_date, "date")}</label></p>
                        </div>
                    </motion.div>
                }
                {
                    overViewLoading ?
                    <SkeletonLoader type={"line"} count={7} currentPage={"overallRiskScore"} />
                    :
                    <motion.div
                        className="h-100"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <div className="overview-chart third-party-dashboard">
                            <p className="heading">Overview</p>
                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <p className="overview-chart-label">Jan</p>
                                {
                                    overallRiskScoreOverview?.stats?.domain_score.length > 0 ?
                                    overallRiskScoreOverview?.stats?.domain_score[0]?.weeks.map((data, index)=>{
                                        return (
                                            <div className={`scan risk-${data?.risk_level}`} key={`jan-${index}`}
                                                onMouseEnter={() => setHoveredItem(`jan-${data.name}`)}
                                                onMouseLeave={() => setHoveredItem(null)}>

                                                {hoveredItem === `jan-${data.name}` && data?.score !== null && <div className="tooltip-container position-absolute">
                                                    <div className="tooltip-content">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="risk-score">
                                                                <p className="score">{data?.score}</p>
                                                                <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                            </div>
                                                            <div className="date-and-difference">
                                                                <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip-arrow"></div>
                                                    </div>
                                                </div>}
                                            </div>
                                        )
                                    })
                                    :
                                    <>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                    </>
                                }
                                
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <p className="overview-chart-label">Feb</p>
                                {
                                    overallRiskScoreOverview?.stats?.domain_score.length > 0 ?
                                    <>
                                        {
                                            overallRiskScoreOverview?.stats?.domain_score[1]?.weeks.map((data, index)=>{
                                                return (
                                                    <div className={`scan risk-${data?.risk_level}`} key={`feb-${index}`} 
                                                        onMouseEnter={() => setHoveredItem(`feb-${data.name}`)}
                                                        onMouseLeave={() => setHoveredItem(null)}>

                                                        {hoveredItem === `feb-${data.name}` && data?.score !== null && <div className="tooltip-container position-absolute">
                                                            <div className="tooltip-content">
                                                                <div className="d-flex align-items-center gap-3">
                                                                    <div className="risk-score">
                                                                        <p className="score">{data?.score}</p>
                                                                        <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                                    </div>
                                                                    <div className="date-and-difference">
                                                                        <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                                    </div>
                                                                </div>
                                                                <div className="tooltip-arrow"></div>
                                                            </div>
                                                        </div>}
                                                    </div>
                                                )
                                            })
                                        }
                                        <div className="scan"></div>
                                    </>
                                    
                                    :
                                    <>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                    </>
                                }
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <p className="overview-chart-label">Mar</p>
                                {
                                    overallRiskScoreOverview?.stats?.domain_score.length > 0 ?
                                    overallRiskScoreOverview?.stats?.domain_score[2]?.weeks.map((data, index)=>{
                                        return (
                                            <div className={`scan risk-${data?.risk_level}`} key={`mar-${index}`}
                                                onMouseEnter={() => setHoveredItem(`mar-${data.name}`)}
                                                onMouseLeave={() => setHoveredItem(null)}>
                                                {hoveredItem === `mar-${data.name}` && data?.score !== null && <div className="tooltip-container position-absolute">
                                                    <div className="tooltip-content">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="risk-score">
                                                                <p className="score">{data?.score}</p>
                                                                <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                            </div>
                                                            <div className="date-and-difference">
                                                                <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip-arrow"></div>
                                                    </div>
                                                </div>}
                                            </div>
                                        )
                                    })
                                    :
                                    <>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                    </>
                                }
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <p className="overview-chart-label">Apr</p>
                                {
                                    overallRiskScoreOverview?.stats?.domain_score.length > 0 ?
                                    overallRiskScoreOverview?.stats?.domain_score[3]?.weeks.map((data, index)=>{
                                        return (
                                            <div className={`scan risk-${data?.risk_level}`} key={`apr-${index}`}
                                                onMouseEnter={() => setHoveredItem(`apr-${data.name}`)}
                                                onMouseLeave={() => setHoveredItem(null)} >
                                                {hoveredItem === `apr-${data.name}` && data?.score !== null && <div className="tooltip-container position-absolute">
                                                    <div className="tooltip-content">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="risk-score">
                                                                <p className="score">{data?.score}</p>
                                                                <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                            </div>
                                                            <div className="date-and-difference">
                                                                <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip-arrow"></div>
                                                    </div>
                                                </div>}
                                            </div>
                                        )
                                    })
                                    :
                                    <>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                    </>
                                }
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <p className="overview-chart-label">May</p>
                                {
                                    overallRiskScoreOverview?.stats?.domain_score.length > 0 ?
                                    overallRiskScoreOverview?.stats?.domain_score[4]?.weeks.map((data, index)=>{
                                        return (
                                            <div className={`scan risk-${data?.risk_level}`} key={`may-${index}`}
                                                onMouseEnter={() => setHoveredItem(`may-${data.name}`)}
                                                onMouseLeave={() => setHoveredItem(null)} >
                                                {hoveredItem === `may-${data.name}` && data?.score !== null && <div className="tooltip-container position-absolute">
                                                    <div className="tooltip-content">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="risk-score">
                                                                <p className="score">{data?.score}</p>
                                                                <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                            </div>
                                                            <div className="date-and-difference">
                                                                <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip-arrow"></div>
                                                    </div>
                                                </div>}
                                            </div>
                                        )
                                    })
                                    :
                                    <>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                    </>
                                }
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <p className="overview-chart-label">Jun</p>
                                {
                                    overallRiskScoreOverview?.stats?.domain_score.length > 0 ?
                                    overallRiskScoreOverview?.stats?.domain_score[5]?.weeks.map((data, index)=>{
                                        return (
                                            <div className={`scan risk-${data?.risk_level}`} key={`jun-${index}`}
                                                onMouseEnter={() => setHoveredItem(`jun-${data.name}`)}
                                                onMouseLeave={() => setHoveredItem(null)}>
                                                {hoveredItem === `jun-${data.name}` && data?.score !== null && <div className="tooltip-container position-absolute">
                                                    <div className="tooltip-content">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="risk-score">
                                                                <p className="score">{data?.score}</p>
                                                                <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                            </div>
                                                            <div className="date-and-difference">
                                                                <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip-arrow"></div>
                                                    </div>
                                                </div>}
                                            </div>
                                        )
                                    })
                                    :
                                    <>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                    </>
                                }
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <p className="overview-chart-label">Jul</p>
                                {
                                    overallRiskScoreOverview?.stats?.domain_score.length > 0 ?
                                    overallRiskScoreOverview?.stats?.domain_score[6]?.weeks.map((data, index)=>{
                                        return (
                                            <div className={`scan risk-${data?.risk_level}`} key={`jul-${index}`}
                                                onMouseEnter={() => setHoveredItem(`jul-${data.name}`)}
                                                onMouseLeave={() => setHoveredItem(null)}>
                                                {hoveredItem === `jul-${data.name}` && data?.score !== null && <div className="tooltip-container position-absolute">
                                                    <div className="tooltip-content">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="risk-score">
                                                                <p className="score">{data?.score}</p>
                                                                <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                            </div>
                                                            <div className="date-and-difference">
                                                                <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip-arrow"></div>
                                                    </div>
                                                </div>}
                                            </div>
                                        )
                                    })
                                    :
                                    <>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                    </>
                                }
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <p className="overview-chart-label">Aug</p>
                                {
                                    overallRiskScoreOverview?.stats?.domain_score.length > 0 ?
                                    overallRiskScoreOverview?.stats?.domain_score[7]?.weeks.map((data, index)=>{
                                        return (
                                            <div className={`scan risk-${data?.risk_level}`} key={`aug-${index}`}
                                                onMouseEnter={() => setHoveredItem(`aug-${data.name}`)}
                                                onMouseLeave={() => setHoveredItem(null)}>
                                                {hoveredItem === `aug-${data.name}` && data?.score !== null && <div className="tooltip-container position-absolute">
                                                    <div className="tooltip-content">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="risk-score">
                                                                <p className="score">{data?.score}</p>
                                                                <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                            </div>
                                                            <div className="date-and-difference">
                                                                <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip-arrow"></div>
                                                    </div>
                                                </div>}
                                            </div>
                                        )
                                    })
                                    :
                                    <>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                    </>
                                }
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <p className="overview-chart-label">Sep</p>
                                {
                                    overallRiskScoreOverview?.stats?.domain_score.length > 0 ?
                                    overallRiskScoreOverview?.stats?.domain_score[8]?.weeks.map((data, index)=>{
                                        return (
                                            <div className={`scan risk-${data?.risk_level}`} key={`sep-${index}`}
                                                onMouseEnter={() => setHoveredItem(`sep-${data.name}`)}
                                                onMouseLeave={() => setHoveredItem(null)}>
                                                {hoveredItem === `sep-${data.name}` && data?.score !== null && <div className="tooltip-container position-absolute">
                                                    <div className="tooltip-content">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="risk-score">
                                                                <p className="score">{data?.score}</p>
                                                                <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                            </div>
                                                            <div className="date-and-difference">
                                                                <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip-arrow"></div>
                                                    </div>
                                                </div>}
                                            </div>
                                        )
                                    })
                                    :
                                    <>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                    </>
                                }
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <p className="overview-chart-label">Oct</p>
                                {
                                    overallRiskScoreOverview?.stats?.domain_score.length > 0 ?
                                    overallRiskScoreOverview?.stats?.domain_score[9]?.weeks.map((data, index)=>{
                                        return (
                                            <div className={`scan risk-${data?.risk_level}`} key={`oct-${index}`}
                                                onMouseEnter={() => setHoveredItem(`oct-${data.name}`)}
                                                onMouseLeave={() => setHoveredItem(null)}>
                                                {hoveredItem === `oct-${data.name}` && data?.score !== null && <div className="tooltip-container position-absolute">
                                                    <div className="tooltip-content">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="risk-score">
                                                                <p className="score">{data?.score}</p>
                                                                <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                            </div>
                                                            <div className="date-and-difference">
                                                                <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip-arrow"></div>
                                                    </div>
                                                </div>}
                                            </div>
                                        )
                                    })
                                    :
                                    <>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                    </>
                                }
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <p className="overview-chart-label">Nov</p>
                                {
                                    overallRiskScoreOverview?.stats?.domain_score.length > 0 ?
                                    overallRiskScoreOverview?.stats?.domain_score[10]?.weeks.map((data, index)=>{
                                        return (
                                            <div className={`scan risk-${data?.risk_level}`} key={`nov-${index}`}
                                                onMouseEnter={() => setHoveredItem(`nov-${data.name}`)}
                                                onMouseLeave={() => setHoveredItem(null)}>
                                                {hoveredItem === `nov-${data.name}` && data?.score !== null && <div className="tooltip-container position-absolute">
                                                    <div className="tooltip-content">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="risk-score">
                                                                <p className="score">{data?.score}</p>
                                                                <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                            </div>
                                                            <div className="date-and-difference">
                                                                <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip-arrow"></div>
                                                    </div>
                                                </div>}
                                            </div>
                                        )
                                    })
                                    :
                                    <>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                    </>
                                }
                            </div>
                            <div className="d-flex align-items-center justify-content-between mt-4">
                                <p className="overview-chart-label">Dec</p>
                                {
                                    overallRiskScoreOverview?.stats?.domain_score.length > 0 ?
                                    overallRiskScoreOverview?.stats?.domain_score[11]?.weeks.map((data, index)=>{
                                        return (
                                            <div className={`scan risk-${data?.risk_level}`} key={`dec-${index}`}
                                                onMouseEnter={() => setHoveredItem(`dec-${data.name}`)}
                                                onMouseLeave={() => setHoveredItem(null)}>
                                                {hoveredItem === `dec-${data.name}` && data?.score !== null && <div className="tooltip-container position-absolute">
                                                    <div className="tooltip-content">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="risk-score">
                                                                <p className="score">{data?.score}</p>
                                                                <p className={`grade ${data?.risk_level}`}>{data?.risk_level}</p>
                                                            </div>
                                                            <div className="date-and-difference">
                                                                <p className="date">{formatDate(data?.scan_date, "date")}</p>
                                                            </div>
                                                        </div>
                                                        <div className="tooltip-arrow"></div>
                                                    </div>
                                                </div>}
                                            </div>
                                        )
                                    })
                                    :
                                    <>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                        <div className="scan no-scan"></div>
                                    </>
                                }
                            </div>
                        </div>
                    </motion.div>                    
                }
            </div>
        </div>
    )
}

export default DomainRiskScore;